import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { mangaList } from '@/mocks/manga';

function ArticleSEO({ manga }: { manga: typeof mangaList[0] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sakuranga.com/' },
          { '@type': 'ListItem', position: 2, name: manga.categoryName, item: `https://sakuranga.com/category/${manga.category}` },
          { '@type': 'ListItem', position: 3, name: manga.title, item: `https://sakuranga.com/article/${manga.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: `${manga.title} - 樱花动漫之家日漫推荐`,
        description: manga.summary,
        author: { '@type': 'Person', name: manga.author },
        datePublished: manga.updateDate,
        url: `https://sakuranga.com/article/${manga.id}`,
        publisher: {
          '@type': 'Organization',
          name: '樱花动漫之家',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://sakuranga.com/article/${manga.id}`,
        },
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const manga = mangaList.find((m) => m.id === Number(id));

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id]);

  if (!manga) {
    return (
      <Layout>
        <div ref={topRef} className="pt-20 pb-14 md:pb-20 min-h-screen">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-700 mb-2">未找到该漫画</h1>
            <p className="text-sm text-foreground-500 mb-6">您查找的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const relatedManga = mangaList
    .filter((m) => m.id !== manga.id && m.category === manga.category)
    .slice(0, 4);

  return (
    <Layout>
      <ArticleSEO manga={manga} />
      <div ref={topRef} className="pt-20 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <span className="text-foreground-300">/</span>
            <Link to={`/category/${manga.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">{manga.categoryName}</Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700">{manga.title}</span>
          </nav>

          {/* Article Content */}
          <article className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 p-5 md:p-8">
              {/* Cover Image */}
              <div className="flex-shrink-0 w-full md:w-64">
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - 日本漫画`}
                    className={`w-full h-full object-cover object-top ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setImageLoaded(true)}
                  />
                  {!imageLoaded && (
                    <div className="absolute inset-0 bg-background-200 animate-pulse" />
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <span className="px-2.5 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                    {manga.categoryName}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                    {manga.status}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-[10px]"></i>
                    </div>
                    {manga.rating}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-foreground-500">
                  <span>
                    <strong className="text-foreground-700 font-medium">作者：</strong>{manga.author}
                  </span>
                  <span>
                    <strong className="text-foreground-700 font-medium">章节：</strong>{manga.chapters}话
                  </span>
                  <span>
                    <strong className="text-foreground-700 font-medium">人气：</strong>{(manga.views / 10000).toFixed(0)}万
                  </span>
                  <span>
                    <strong className="text-foreground-700 font-medium">更新：</strong>{manga.updateDate}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mt-4">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200/70"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-heading font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{manga.summary}</p>
                </div>

                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => navigate(`/category/${manga.category}`)}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-book-open-line text-sm"></i>
                    </div>
                    浏览更多{manga.categoryName}
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-home-line text-sm"></i>
                    </div>
                    返回首页
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Related Manga */}
          {relatedManga.length > 0 && (
            <section className="mt-12 md:mt-16">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/article/${rm.id}`}
                    className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={rm.cover}
                        alt={rm.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500/90 text-background-50 text-xs font-medium">
                        {rm.rating}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {rm.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{rm.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
}